// features/procurement/VendorsPage.tsx
/**
 * Vendor & Supplier Management — Executive UI/UX Redesign
 * Tabbed structured form modal, card & table perspectives, instant search,
 * and robust zero-silent-fail validation.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card } from '@/components/ui/Card';
import {
    Truck, Plus, Search, X, Save, Pencil, Trash2, Star,
    Phone, Mail, Building2, CreditCard, Clock,
    LayoutGrid, List, RefreshCw, CheckCircle, Ban,
    Users, ChevronRight, Globe, MapPin, ExternalLink,
    ShieldCheck, Sparkles, AlertCircle, ArrowRight, Store, Upload
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getVendors,
    createVendor,
    updateVendor,
    deleteVendor,
    getProcurementErrorMessage,
    Vendor,
} from './services/procurementApi';
import { VendorBulkUploadModal } from './components/VendorBulkUploadModal';

// ============================================================
// CONSTANTS & CATEGORIES
// ============================================================

type ViewMode = 'card' | 'table';

const VENDOR_CATEGORIES = [
    'All Categories',
    'Food & Beverages',
    'Pharmaceuticals',
    'Electronics',
    'Clothing & Textiles',
    'Building Materials',
    'Office Supplies',
    'Cleaning & Hygiene',
    'Agricultural',
    'Automotive',
    'Packaging',
    'Other',
];

const PAYMENT_TERMS_OPTIONS = [
    'Cash on Delivery',
    'Net 7',
    'Net 15',
    'Net 30',
    'Net 60',
    'Prepayment',
];

const AVATAR_GRADIENTS = [
    'from-teal-500 to-cyan-600',
    'from-blue-500 to-indigo-600',
    'from-amber-500 to-orange-600',
    'from-emerald-500 to-teal-600',
    'from-purple-500 to-violet-600',
    'from-rose-500 to-pink-600',
];

function StarRating({ rating }: { rating: number }) {
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
                <Star
                    key={star}
                    size={12}
                    className={clsx(star <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200')}
                />
            ))}
        </div>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function VendorsPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [viewMode, setViewMode] = useState<ViewMode>('card');
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All Categories');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'deactivated'>('all');
    const [showForm, setShowForm] = useState(false);
    const [formTab, setFormTab] = useState<'general' | 'contact' | 'financial'>('general');
    const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Vendor | null>(null);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

    // Form State
    const [form, setForm] = useState({
        name: '',
        contactPerson: '',
        email: '',
        phone: '',
        alternatePhone: '',
        website: '',
        address: '',
        city: '',
        state: '',
        taxId: '',
        paymentTerms: 'Net 30',
        creditLimit: '',
        bankName: '',
        bankAccountNumber: '',
        bankAccountName: '',
        category: 'Food & Beverages',
        leadTimeDays: '7',
        rating: '5',
        isPreferred: false,
        isActive: true,
        notes: '',
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // ============================================================
    // QUERIES & MUTATIONS
    // ============================================================

    const { data: vendors, isLoading, isError, refetch } = useQuery<Vendor[]>({
        queryKey: ['vendors', tenantId],
        queryFn: () => getVendors(tenantId),
        enabled: !!tenantId,
    });

    const saveMutation = useMutation({
        mutationFn: (payload: Partial<Vendor>) =>
            editingVendor
                ? updateVendor(tenantId, editingVendor.id, payload)
                : createVendor(tenantId, payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vendors'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-vendors'] });
            addToast({
                type: 'success',
                title: editingVendor ? 'Supplier Updated' : 'Supplier Registered',
                message: editingVendor ? `${editingVendor.name} updated successfully.` : 'New vendor profile created.',
            });
            closeForm();
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Save Failed',
                message: getProcurementErrorMessage(err, 'Failed to save vendor details. Please verify your inputs.'),
            });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (vendorId: string) => deleteVendor(tenantId, vendorId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['vendors'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-vendors'] });
            setDeleteConfirm(null);
            addToast({
                type: 'success',
                title: 'Supplier Deactivated',
                message: 'Vendor has been archived and hidden from new purchase orders.',
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Deactivation Failed',
                message: getProcurementErrorMessage(err, 'Could not deactivate vendor.'),
            });
        },
    });

    const reactivateMutation = useMutation({
        mutationFn: (vendorId: string) => updateVendor(tenantId, vendorId, { isActive: true }),
        onSuccess: (_data, vendorId) => {
            queryClient.invalidateQueries({ queryKey: ['vendors'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-vendors'] });
            const vendorName = vendorsList.find(v => v.id === vendorId)?.name || 'Supplier';
            addToast({
                type: 'success',
                title: 'Supplier Reactivated',
                message: `${vendorName} is now active and available for purchase orders.`,
            });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Reactivation Failed',
                message: getProcurementErrorMessage(err, 'Could not reactivate vendor.'),
            });
        },
    });

    // ============================================================
    // HANDLERS
    // ============================================================

    const openCreateForm = () => {
        setEditingVendor(null);
        setForm({
            name: '',
            contactPerson: '',
            email: '',
            phone: '',
            alternatePhone: '',
            website: '',
            address: '',
            city: '',
            state: '',
            taxId: '',
            paymentTerms: 'Net 30',
            creditLimit: '',
            bankName: '',
            bankAccountNumber: '',
            bankAccountName: '',
            category: 'Food & Beverages',
            leadTimeDays: '7',
            rating: '5',
            isPreferred: false,
            isActive: true,
            notes: '',
        });
        setFormErrors({});
        setFormTab('general');
        setShowForm(true);
    };

    const handleEdit = (vendor: Vendor) => {
        setEditingVendor(vendor);
        setForm({
            name: vendor.name || '',
            contactPerson: vendor.contactPerson || '',
            email: vendor.email || '',
            phone: vendor.phone || '',
            alternatePhone: vendor.alternatePhone || '',
            website: vendor.website || '',
            address: vendor.address || '',
            city: vendor.city || '',
            state: vendor.state || '',
            taxId: vendor.taxId || '',
            paymentTerms: vendor.paymentTerms || 'Net 30',
            creditLimit: vendor.creditLimit || '',
            bankName: vendor.bankName || '',
            bankAccountNumber: vendor.bankAccountNumber || '',
            bankAccountName: vendor.bankAccountName || '',
            category: vendor.category || 'Food & Beverages',
            leadTimeDays: String(vendor.leadTimeDays || 7),
            rating: String(vendor.rating || 5),
            isPreferred: Boolean(vendor.isPreferred),
            isActive: vendor.isActive !== false,
            notes: vendor.notes || '',
        });
        setFormErrors({});
        setFormTab('general');
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingVendor(null);
        setFormErrors({});
    };

    const validateForm = () => {
        const errors: Record<string, string> = {};
        if (!form.name.trim()) errors.name = 'Supplier legal name is required';
        if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            errors.email = 'Please provide a valid email address';
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) {
            addToast({
                type: 'error',
                title: 'Validation Error',
                message: 'Please resolve required fields before saving.',
            });
            return;
        }

        const payload: any = {
            name: form.name.trim(),
            contactPerson: form.contactPerson.trim() || undefined,
            email: form.email.trim() || undefined,
            phone: form.phone.trim() || undefined,
            alternatePhone: form.alternatePhone.trim() || undefined,
            website: form.website.trim() || undefined,
            address: form.address.trim() || undefined,
            city: form.city.trim() || undefined,
            state: form.state.trim() || undefined,
            taxId: form.taxId.trim() || undefined,
            paymentTerms: form.paymentTerms || undefined,
            creditLimit: form.creditLimit ? form.creditLimit : undefined,
            bankName: form.bankName.trim() || undefined,
            bankAccountNumber: form.bankAccountNumber.trim() || undefined,
            bankAccountName: form.bankAccountName.trim() || undefined,
            category: form.category || undefined,
            leadTimeDays: parseInt(form.leadTimeDays) || 7,
            rating: parseInt(form.rating) || 5,
            isPreferred: form.isPreferred,
            isActive: form.isActive,
            notes: form.notes.trim() || undefined,
        };

        saveMutation.mutate(payload);
    };

    // ============================================================
    // FILTERING & STATS
    // ============================================================

    const vendorsList = vendors || [];

    const filteredVendors = useMemo(() => {
        return vendorsList.filter((v) => {
            const matchesSearch =
                !searchQuery ||
                v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                v.contactPerson?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                v.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                v.phone?.toLowerCase().includes(searchQuery.toLowerCase());

            const matchesCategory =
                categoryFilter === 'All Categories' || v.category === categoryFilter;

            const matchesStatus =
                statusFilter === 'all' ||
                (statusFilter === 'active' && v.isActive !== false) ||
                (statusFilter === 'deactivated' && v.isActive === false);

            return matchesSearch && matchesCategory && matchesStatus;
        });
    }, [vendorsList, searchQuery, categoryFilter, statusFilter]);

    const activeCount = vendorsList.filter((v) => v.isActive !== false).length;
    const deactivatedCount = vendorsList.filter((v) => v.isActive === false).length;
    const preferredCount = vendorsList.filter((v) => v.isPreferred).length;
    const avgLeadTime = vendorsList.length > 0
        ? Math.round(vendorsList.reduce((acc, v) => acc + (v.leadTimeDays || 7), 0) / vendorsList.length)
        : 7;

    return (
        <div className="min-h-screen bg-slate-50/60 pb-16">
            {/* Header */}
            <div className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1680px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/procurement')}
                                className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition"
                                type="button"
                                title="Back to Procurement"
                            >
                                <ChevronRight size={20} className="rotate-180" />
                            </button>
                            <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl shadow-lg shadow-teal-500/20 text-white">
                                <Store className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-slate-900 tracking-tight">Suppliers Directory</h1>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {vendorsList.length} total registered partners • {preferredCount} preferred
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {/* View Switcher */}
                            <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200">
                                <button
                                    onClick={() => setViewMode('card')}
                                    className={clsx(
                                        'p-2 rounded-lg transition-all',
                                        viewMode === 'card' ? 'bg-white shadow text-teal-700' : 'text-slate-500 hover:text-slate-800'
                                    )}
                                    title="Grid View"
                                    type="button"
                                >
                                    <LayoutGrid size={16} />
                                </button>
                                <button
                                    onClick={() => setViewMode('table')}
                                    className={clsx(
                                        'p-2 rounded-lg transition-all',
                                        viewMode === 'table' ? 'bg-white shadow text-teal-700' : 'text-slate-500 hover:text-slate-800'
                                    )}
                                    title="Table View"
                                    type="button"
                                >
                                    <List size={16} />
                                </button>
                            </div>

                            <button
                                onClick={() => setShowBulkUploadModal(true)}
                                className="px-4 py-2.5 bg-white border border-slate-200 hover:border-teal-400 hover:bg-slate-50 text-slate-700 rounded-xl transition font-bold text-xs flex items-center gap-2 shadow-sm"
                                type="button"
                            >
                                <Upload size={15} className="text-teal-600" />
                                <span>Bulk Upload</span>
                            </button>

                            <button
                                onClick={openCreateForm}
                                className="px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl hover:from-teal-700 hover:to-emerald-700 transition font-bold text-xs flex items-center gap-2 shadow-md shadow-teal-500/25"
                                type="button"
                            >
                                <Plus size={16} />
                                <span>Register Supplier</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1680px] mx-auto px-6 lg:px-8 pt-6 space-y-6">

                {/* Quick Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Active Suppliers</span>
                        <p className="text-2xl font-black text-slate-900 mt-1">{vendorsList.length}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Preferred Partners</span>
                        <p className="text-2xl font-black text-amber-600 mt-1 flex items-center gap-2">
                            <span>{preferredCount}</span>
                            <Star size={18} className="fill-amber-400 text-amber-400" />
                        </p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Avg Lead Time</span>
                        <p className="text-2xl font-black text-teal-700 mt-1">{avgLeadTime} Days</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Categories</span>
                        <p className="text-2xl font-black text-slate-900 mt-1">{VENDOR_CATEGORIES.length - 1}</p>
                    </div>
                </div>

                {/* Search & Filter Bar */}
                <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
                        <div className="relative w-full sm:w-80">
                            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search by name, contact, phone, email..."
                                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition"
                            />
                        </div>

                        {/* Status Filter Tabs */}
                        <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
                            <button
                                onClick={() => setStatusFilter('all')}
                                className={clsx(
                                    'px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap',
                                    statusFilter === 'all'
                                        ? 'bg-white text-slate-900 shadow-xs'
                                        : 'text-slate-500 hover:text-slate-800'
                                )}
                                type="button"
                            >
                                All ({vendorsList.length})
                            </button>
                            <button
                                onClick={() => setStatusFilter('active')}
                                className={clsx(
                                    'px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5',
                                    statusFilter === 'active'
                                        ? 'bg-white text-emerald-700 shadow-xs'
                                        : 'text-slate-500 hover:text-slate-800'
                                )}
                                type="button"
                            >
                                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                                Active ({activeCount})
                            </button>
                            <button
                                onClick={() => setStatusFilter('deactivated')}
                                className={clsx(
                                    'px-3 py-1.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5',
                                    statusFilter === 'deactivated'
                                        ? 'bg-white text-rose-700 shadow-xs'
                                        : 'text-slate-500 hover:text-slate-800'
                                )}
                                type="button"
                            >
                                <span className="w-2 h-2 rounded-full bg-rose-500 inline-block" />
                                Deactivated ({deactivatedCount})
                            </button>
                        </div>
                    </div>

                    {/* Category Carousel */}
                    <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
                        {VENDOR_CATEGORIES.slice(0, 7).map((cat) => (
                            <button
                                key={cat}
                                onClick={() => setCategoryFilter(cat)}
                                className={clsx(
                                    'px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all',
                                    categoryFilter === cat
                                        ? 'bg-teal-600 text-white shadow-sm'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                )}
                                type="button"
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content View */}
                {isLoading ? (
                    <div className="py-20 text-center flex flex-col items-center justify-center">
                        <Spinner size="lg" />
                        <p className="text-sm font-medium text-slate-500 mt-4">Loading suppliers catalog...</p>
                    </div>
                ) : filteredVendors.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-teal-50 text-teal-600 flex items-center justify-center mx-auto mb-4">
                            <Store size={28} />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">No Suppliers Found</h3>
                        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                            {searchQuery ? `No vendor matching "${searchQuery}".` : 'Get started by onboarding your suppliers and contractors.'}
                        </p>
                        <button
                            onClick={openCreateForm}
                            className="mt-5 px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            type="button"
                        >
                            + Register First Supplier
                        </button>
                    </div>
                ) : viewMode === 'card' ? (
                    /* ============================================================ */
                    /* CARDS PERSPECTIVE                                            */
                    /* ============================================================ */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filteredVendors.map((vendor, idx) => {
                            const gradient = AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length];
                            return (
                                <motion.div
                                    key={vendor.id}
                                    whileHover={{ y: -3 }}
                                    className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between group"
                                >
                                    <div>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} text-white flex items-center justify-center font-black text-base shadow-sm`}>
                                                    {vendor.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-sm text-slate-900 group-hover:text-teal-700 transition">
                                                        {vendor.name}
                                                    </h3>
                                                    <p className="text-xs text-slate-400 mt-0.5">{vendor.category || 'General Supplies'}</p>
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                                {vendor.isPreferred && (
                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1">
                                                        <Star size={10} className="fill-amber-400 text-amber-400" />
                                                        <span>Preferred</span>
                                                    </span>
                                                )}
                                                <span className={clsx(
                                                    'px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider',
                                                    vendor.isActive !== false
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                        : 'bg-rose-50 text-rose-700 border-rose-200'
                                                )}>
                                                    {vendor.isActive !== false ? 'Active' : 'Deactivated'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Contact & Terms Strip */}
                                        <div className="mt-4 pt-3 border-t border-slate-100 space-y-2 text-xs text-slate-600">
                                            {vendor.contactPerson && (
                                                <div className="flex items-center gap-2">
                                                    <Users size={13} className="text-slate-400" />
                                                    <span>{vendor.contactPerson}</span>
                                                </div>
                                            )}
                                            {vendor.phone && (
                                                <div className="flex items-center gap-2">
                                                    <Phone size={13} className="text-slate-400" />
                                                    <a href={`tel:${vendor.phone}`} className="hover:text-teal-600 transition">
                                                        {vendor.phone}
                                                    </a>
                                                </div>
                                            )}
                                            {vendor.email && (
                                                <div className="flex items-center gap-2 truncate">
                                                    <Mail size={13} className="text-slate-400 flex-shrink-0" />
                                                    <a href={`mailto:${vendor.email}`} className="hover:text-teal-600 transition truncate">
                                                        {vendor.email}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Footer Actions */}
                                    <div className="mt-5 pt-3 border-t border-slate-100 flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-medium">
                                                {vendor.paymentTerms || 'Net 30'}
                                            </span>
                                            <StarRating rating={vendor.rating || 5} />
                                        </div>

                                        <div className="flex items-center gap-1.5">
                                            <button
                                                onClick={() => handleEdit(vendor)}
                                                className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                                                title="Edit Supplier"
                                                type="button"
                                            >
                                                <Pencil size={14} />
                                            </button>
                                            {vendor.isActive !== false ? (
                                                <button
                                                    onClick={() => setDeleteConfirm(vendor)}
                                                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                                                    title="Deactivate Supplier"
                                                    type="button"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => reactivateMutation.mutate(vendor.id)}
                                                    disabled={reactivateMutation.isPending}
                                                    className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-[10px] font-bold transition flex items-center gap-1"
                                                    title="Reactivate Supplier"
                                                    type="button"
                                                >
                                                    <CheckCircle size={12} />
                                                    <span>Reactivate</span>
                                                </button>
                                            )}
                                            <button
                                                onClick={() => navigate(`/vendors/${vendor.id}`)}
                                                className="px-2.5 py-1 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition flex items-center gap-1"
                                                type="button"
                                            >
                                                <span>Profile</span>
                                                <ChevronRight size={13} />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                ) : (
                    /* ============================================================ */
                    /* TABLE PERSPECTIVE                                            */
                    /* ============================================================ */
                    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/70">
                                        <th className="py-3.5 px-6">Supplier</th>
                                        <th className="py-3.5 px-6">Category</th>
                                        <th className="py-3.5 px-6">Contact</th>
                                        <th className="py-3.5 px-6">Terms</th>
                                        <th className="py-3.5 px-6">Rating</th>
                                        <th className="py-3.5 px-6 text-center">Status</th>
                                        <th className="py-3.5 px-6 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {filteredVendors.map((vendor) => (
                                        <tr key={vendor.id} className="hover:bg-slate-50/80 transition-colors">
                                            <td className="py-3.5 px-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                        {vendor.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <span className="font-bold text-slate-900 block">{vendor.name}</span>
                                                        {vendor.isPreferred && (
                                                            <span className="text-[10px] font-bold text-amber-600">Preferred Supplier</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3.5 px-6 text-xs text-slate-600">{vendor.category || 'General'}</td>
                                            <td className="py-3.5 px-6 text-xs text-slate-600">
                                                <p className="font-medium text-slate-800">{vendor.contactPerson || '—'}</p>
                                                <p className="text-slate-400">{vendor.phone || vendor.email || ''}</p>
                                            </td>
                                            <td className="py-3.5 px-6 text-xs">
                                                <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded font-medium">
                                                    {vendor.paymentTerms || 'Net 30'}
                                                </span>
                                            </td>
                                            <td className="py-3.5 px-6">
                                                <StarRating rating={vendor.rating || 5} />
                                            </td>
                                            <td className="py-3.5 px-6 text-center">
                                                <span className={clsx(
                                                    'px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider',
                                                    vendor.isActive !== false
                                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                        : 'bg-rose-50 text-rose-700 border-rose-200'
                                                )}>
                                                    {vendor.isActive !== false ? 'Active' : 'Deactivated'}
                                                </span>
                                            </td>
                                            <td className="py-3.5 px-6 text-right">
                                                <div className="flex items-center justify-end gap-1.5">
                                                    <button
                                                        onClick={() => handleEdit(vendor)}
                                                        className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                                                        title="Edit Supplier"
                                                        type="button"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    {vendor.isActive !== false ? (
                                                        <button
                                                            onClick={() => setDeleteConfirm(vendor)}
                                                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                                                            title="Deactivate Supplier"
                                                            type="button"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => reactivateMutation.mutate(vendor.id)}
                                                            disabled={reactivateMutation.isPending}
                                                            className="px-2 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg text-[10px] font-bold transition flex items-center gap-1"
                                                            title="Reactivate Supplier"
                                                            type="button"
                                                        >
                                                            <CheckCircle size={12} />
                                                            <span>Reactivate</span>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => navigate(`/vendors/${vendor.id}`)}
                                                        className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition"
                                                        title="View Details"
                                                        type="button"
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* ============================================================ */}
            {/* ELEVATED ADD / EDIT SUPPLIER MODAL                           */}
            {/* ============================================================ */}
            <AnimatePresence>
                {showForm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
                        onClick={closeForm}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col my-8"
                        >
                            {/* Modal Header */}
                            <div className="bg-gradient-to-r from-teal-600 via-teal-700 to-cyan-700 px-6 py-4 text-white flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-xl bg-white/20 backdrop-blur-md">
                                        <Building2 size={20} />
                                    </div>
                                    <div>
                                        <h2 className="text-base font-bold">
                                            {editingVendor ? `Edit ${editingVendor.name}` : 'Register New Supplier'}
                                        </h2>
                                        <p className="text-teal-100 text-xs mt-0.5">
                                            Complete supplier profile for procurement & inventory tracking
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={closeForm}
                                    className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition"
                                    type="button"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Tab Switcher */}
                            <div className="flex items-center border-b border-slate-200 px-6 bg-slate-50/60">
                                <button
                                    type="button"
                                    onClick={() => setFormTab('general')}
                                    className={clsx(
                                        'py-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-2',
                                        formTab === 'general'
                                            ? 'border-teal-600 text-teal-700 bg-white'
                                            : 'border-transparent text-slate-500 hover:text-slate-800'
                                    )}
                                >
                                    <Store size={14} />
                                    <span>General Info</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormTab('contact')}
                                    className={clsx(
                                        'py-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-2',
                                        formTab === 'contact'
                                            ? 'border-teal-600 text-teal-700 bg-white'
                                            : 'border-transparent text-slate-500 hover:text-slate-800'
                                    )}
                                >
                                    <MapPin size={14} />
                                    <span>Contact & Location</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormTab('financial')}
                                    className={clsx(
                                        'py-3 px-4 text-xs font-bold border-b-2 transition-all flex items-center gap-2',
                                        formTab === 'financial'
                                            ? 'border-teal-600 text-teal-700 bg-white'
                                            : 'border-transparent text-slate-500 hover:text-slate-800'
                                    )}
                                >
                                    <CreditCard size={14} />
                                    <span>Terms & Banking</span>
                                </button>
                            </div>

                            {/* Form Body */}
                            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                {formTab === 'general' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                Company / Supplier Name <span className="text-rose-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                placeholder="e.g., Nestle Nigeria Plc"
                                                className={clsx(
                                                    'w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition',
                                                    formErrors.name
                                                        ? 'border-rose-400 focus:ring-rose-500/20'
                                                        : 'border-slate-200 focus:border-teal-500 focus:ring-teal-500/20'
                                                )}
                                                autoFocus
                                            />
                                            {formErrors.name && (
                                                <p className="text-xs text-rose-500 font-semibold mt-1">{formErrors.name}</p>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Industry Category
                                                </label>
                                                <select
                                                    value={form.category}
                                                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 bg-white"
                                                >
                                                    {VENDOR_CATEGORIES.filter((c) => c !== 'All Categories').map((c) => (
                                                        <option key={c} value={c}>{c}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Delivery Lead Time (Days)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={form.leadTimeDays}
                                                    onChange={(e) => setForm({ ...form, leadTimeDays: e.target.value })}
                                                    min="1"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Supplier Rating (1-5)
                                                </label>
                                                <select
                                                    value={form.rating}
                                                    onChange={(e) => setForm({ ...form, rating: e.target.value })}
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 bg-white"
                                                >
                                                    {[5, 4, 3, 2, 1].map((r) => (
                                                        <option key={r} value={r}>{r} Star{r > 1 ? 's' : ''}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 pt-4 sm:pt-6">
                                                <label className="flex items-center gap-2.5 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.isPreferred}
                                                        onChange={(e) => setForm({ ...form, isPreferred: e.target.checked })}
                                                        className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 border-slate-300"
                                                    />
                                                    <span className="text-xs font-bold text-slate-800">
                                                        Preferred Supplier
                                                    </span>
                                                </label>
                                                <label className="flex items-center gap-2.5 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={form.isActive}
                                                        onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                                        className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 border-slate-300"
                                                    />
                                                    <span className="text-xs font-bold text-slate-800">
                                                        Active Supplier (accepts purchase orders)
                                                    </span>
                                                </label>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                Internal Notes
                                            </label>
                                            <textarea
                                                value={form.notes}
                                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                                rows={2}
                                                placeholder="e.g., Primary bread supplier for Victoria Island branch"
                                                className="w-full px-4 py-2 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 resize-none"
                                            />
                                        </div>
                                    </div>
                                )}

                                {formTab === 'contact' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Contact Representative
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.contactPerson}
                                                    onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
                                                    placeholder="Account Manager / Sales Rep"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Primary Phone
                                                </label>
                                                <input
                                                    type="tel"
                                                    value={form.phone}
                                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                                    placeholder="+234 800 000 0000"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Email Address
                                                </label>
                                                <input
                                                    type="email"
                                                    value={form.email}
                                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                    placeholder="orders@supplier.com"
                                                    className={clsx(
                                                        'w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 transition',
                                                        formErrors.email
                                                            ? 'border-rose-400 focus:ring-rose-500/20'
                                                            : 'border-slate-200 focus:border-teal-500 focus:ring-teal-500/20'
                                                    )}
                                                />
                                                {formErrors.email && (
                                                    <p className="text-xs text-rose-500 font-semibold mt-1">{formErrors.email}</p>
                                                )}
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Website
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.website}
                                                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                                                    placeholder="https://..."
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                Warehouse / Office Address
                                            </label>
                                            <input
                                                type="text"
                                                value={form.address}
                                                onChange={(e) => setForm({ ...form, address: e.target.value })}
                                                placeholder="Plot 10, Industrial Layout..."
                                                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    City
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.city}
                                                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                                                    placeholder="Ikeja"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    State / Region
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.state}
                                                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                                                    placeholder="Lagos"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {formTab === 'financial' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Standard Payment Terms
                                                </label>
                                                <select
                                                    value={form.paymentTerms}
                                                    onChange={(e) => setForm({ ...form, paymentTerms: e.target.value })}
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 bg-white"
                                                >
                                                    {PAYMENT_TERMS_OPTIONS.map((t) => (
                                                        <option key={t} value={t}>{t}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Credit Limit (₦)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.creditLimit}
                                                    onChange={(e) => setForm({ ...form, creditLimit: e.target.value })}
                                                    placeholder="5000000"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Tax Identification Number (TIN / RC)
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.taxId}
                                                    onChange={(e) => setForm({ ...form, taxId: e.target.value })}
                                                    placeholder="TIN-10928374"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Bank Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.bankName}
                                                    onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                                                    placeholder="Access Bank / Zenith Bank"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Bank Account Number
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.bankAccountNumber}
                                                    onChange={(e) => setForm({ ...form, bankAccountNumber: e.target.value })}
                                                    placeholder="0123456789"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                                                    Bank Account Beneficiary Name
                                                </label>
                                                <input
                                                    type="text"
                                                    value={form.bankAccountName}
                                                    onChange={(e) => setForm({ ...form, bankAccountName: e.target.value })}
                                                    placeholder="Legal Business Name"
                                                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Modal Footer */}
                                <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        {formTab !== 'general' && (
                                            <button
                                                type="button"
                                                onClick={() => setFormTab(formTab === 'financial' ? 'contact' : 'general')}
                                                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900 transition"
                                            >
                                                Back
                                            </button>
                                        )}
                                        {formTab !== 'financial' && (
                                            <button
                                                type="button"
                                                onClick={() => setFormTab(formTab === 'general' ? 'contact' : 'financial')}
                                                className="px-4 py-2 text-xs font-bold text-teal-700 hover:underline transition"
                                            >
                                                Next Step →
                                            </button>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2.5">
                                        <button
                                            type="button"
                                            onClick={closeForm}
                                            className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={saveMutation.isPending}
                                            className="px-5 py-2 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl text-xs font-bold shadow-md shadow-teal-500/20 hover:from-teal-700 hover:to-emerald-700 transition flex items-center gap-2 disabled:opacity-50"
                                        >
                                            {saveMutation.isPending ? <Spinner size="xs" variant="white" /> : <Save size={14} />}
                                            <span>{editingVendor ? 'Update Supplier' : 'Save Supplier'}</span>
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ============================================================ */}
            {/* DEACTIVATE / DELETE CONFIRMATION MODAL                       */}
            {/* ============================================================ */}
            <AnimatePresence>
                {deleteConfirm && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setDeleteConfirm(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center"
                        >
                            <div className="w-14 h-14 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mx-auto mb-4">
                                <Trash2 size={24} />
                            </div>
                            <h3 className="text-base font-bold text-slate-900">
                                Deactivate "{deleteConfirm.name}"?
                            </h3>
                            <p className="text-xs text-slate-500 mt-2">
                                This supplier will be archived and hidden from new purchase orders. Historical transactions, GRNs, and invoices will remain intact. You can reactivate this supplier anytime from the Deactivated filter.
                            </p>
                            <div className="flex items-center gap-3 mt-6">
                                <button
                                    onClick={() => setDeleteConfirm(null)}
                                    className="flex-1 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => deleteMutation.mutate(deleteConfirm.id)}
                                    disabled={deleteMutation.isPending}
                                    className="flex-1 py-2.5 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-xl text-xs font-bold hover:from-rose-600 transition shadow-md shadow-rose-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
                                    type="button"
                                >
                                    {deleteMutation.isPending ? <Spinner size="xs" variant="white" /> : <Trash2 size={14} />}
                                    <span>Deactivate</span>
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Vendor Bulk Upload Modal */}
            <VendorBulkUploadModal
                isOpen={showBulkUploadModal}
                onClose={() => setShowBulkUploadModal(false)}
                onSuccess={() => refetch()}
            />
        </div>
    );
}